import {
  type MemberRole,
  type NotificationSettings as ApiNotificationSettings,
  type Severity
} from 'shared/api/clients'
import { type MessageResp } from 'shared/api/messages'
import PERMISSIONS from 'shared/constants/permissions'
import { type NotificationsSettingsFormData, type WorkspaceDetailsTabId } from 'src/workspace/types'

export interface WorkspaceMemberIdentity {
  id: number
  name: string
  email: string
  role: MemberRole
}

export interface RoleTransitionOption {
  target: MemberRole
  permission: readonly MemberRole[]
}

const MEMBER_ROLES: MemberRole[] = ['viewer', 'editor', 'admin']
const SEVERITIES: Severity[] = ['critical', 'moderate', 'low']

/** Shared invite/email format check used by the invite UI and copilot tools. */
export const INVITE_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export const isMemberRole = (value: string): value is MemberRole => {
  return MEMBER_ROLES.includes(value as MemberRole)
}

export const isWorkspaceDetailsTabId = (value: string): value is WorkspaceDetailsTabId => {
  return value === 'members' || value === 'notification-settings'
}

export const isSeverity = (value: string): value is Severity => {
  return SEVERITIES.includes(value as Severity)
}

export const isValidInviteEmailFormat = (email: string): boolean => {
  return INVITE_EMAIL_PATTERN.test(email.trim())
}

export const canUserInviteMembers = (currentUserRole: MemberRole | undefined): boolean => {
  return (
    currentUserRole != null && (PERMISSIONS.INVITE_MEMBERS_WORKSPACE as readonly MemberRole[]).includes(currentUserRole)
  )
}

export const canUserInviteAsRole = (currentUserRole: MemberRole | undefined, inviteRole: MemberRole): boolean => {
  if (!canUserInviteMembers(currentUserRole)) return false
  if (inviteRole === 'admin') return currentUserRole === 'admin'
  return true
}

export const canUserRevokePendingInvites = (currentUserRole: MemberRole | undefined): boolean => {
  return (
    currentUserRole != null &&
    (PERMISSIONS.REVOKE_PENDING_INVITATIONS_WORKSPACE as readonly MemberRole[]).includes(currentUserRole)
  )
}

export const normalizeEmail = (email: string): string => email.trim().toLowerCase()

export const isEmailAlreadyMember = (email: string, memberEmails: readonly string[]): boolean => {
  const needle = normalizeEmail(email)
  if (needle.length === 0) return false
  return memberEmails.some((memberEmail) => normalizeEmail(memberEmail) === needle)
}

/**
 * True when `email` appears more than once in the invite list (case-insensitive).
 */
export const isDuplicateInviteEmail = (email: string, inviteEmails: readonly string[]): boolean => {
  const needle = normalizeEmail(email)
  if (needle.length === 0) return false
  return inviteEmails.filter((inviteEmail) => normalizeEmail(inviteEmail) === needle).length >= 2
}

export const getPendingInvites = (messages: MessageResp[] | undefined): MessageResp[] => {
  if (messages == null) return []
  return messages.filter((message) => message.status === 'pending')
}

export const countMembersByRole = (members: WorkspaceMemberIdentity[]): Record<MemberRole, number> => {
  return {
    viewer: members.filter((member) => member.role === 'viewer').length,
    editor: members.filter((member) => member.role === 'editor').length,
    admin: members.filter((member) => member.role === 'admin').length
  }
}

/**
 * Sorts members the same way as the members table name column (`localeCompare`).
 */
export const sortWorkspaceMembers = <T extends { name: string }>(
  members: readonly T[],
  sort: { id: 'name'; desc: boolean }
): T[] => {
  return [...members].sort((a, b) => {
    const comparison = a.name.localeCompare(b.name)
    return sort.desc ? -comparison : comparison
  })
}

export const countAdmins = (members: WorkspaceMemberIdentity[]): number => {
  return members.filter((member) => member.role === 'admin').length
}

/**
 * Role transitions and the permission required to perform each one.
 * Single source of truth for AssignNewRole UI and copilot tools.
 */
export const getRoleTransitions = (currentRole: MemberRole): RoleTransitionOption[] => {
  const transitions: Record<MemberRole, RoleTransitionOption[]> = {
    viewer: [
      { target: 'editor', permission: PERMISSIONS.ASSIGN_AS_EDITOR_WORKSPACE as readonly MemberRole[] },
      { target: 'admin', permission: PERMISSIONS.ASSIGN_AS_ADMIN_WORKSPACE as readonly MemberRole[] }
    ],
    editor: [
      { target: 'viewer', permission: PERMISSIONS.ASSIGN_AS_VIEWER_WORKSPACE as readonly MemberRole[] },
      { target: 'admin', permission: PERMISSIONS.ASSIGN_AS_ADMIN_WORKSPACE as readonly MemberRole[] }
    ],
    admin: [
      { target: 'editor', permission: PERMISSIONS.ASSIGN_AS_ADMIN_WORKSPACE as readonly MemberRole[] },
      { target: 'viewer', permission: PERMISSIONS.ASSIGN_AS_ADMIN_WORKSPACE as readonly MemberRole[] }
    ]
  }
  return transitions[currentRole]
}

export const getAllowedRoleTransitions = (currentRole: MemberRole): MemberRole[] => {
  return getRoleTransitions(currentRole).map((option) => option.target)
}

export const getAuthorizedRoleTransitions = (
  memberRole: MemberRole,
  actorRole: MemberRole | undefined
): RoleTransitionOption[] => {
  if (actorRole == null) return []
  return getRoleTransitions(memberRole).filter((option) => option.permission.includes(actorRole))
}

export const canAssignMemberRole = ({
  member,
  newRole,
  members,
  currentUserId,
  currentUserRole,
  isOnlyMember
}: {
  member: WorkspaceMemberIdentity
  newRole: MemberRole
  members: WorkspaceMemberIdentity[]
  currentUserId: number | undefined
  currentUserRole: MemberRole | undefined
  isOnlyMember: boolean
}): { allowed: true } | { allowed: false; reason: string } => {
  if (currentUserRole == null) {
    return { allowed: false, reason: 'Unable to determine your role in this workspace.' }
  }

  if (member.role === newRole) {
    return { allowed: false, reason: `${member.name} is already a ${newRole}.` }
  }

  if (isOnlyMember) {
    return { allowed: false, reason: 'Cannot change roles when you are the only member of the workspace.' }
  }

  const authorized = getAuthorizedRoleTransitions(member.role, currentUserRole)
  if (!authorized.some((option) => option.target === newRole)) {
    return {
      allowed: false,
      reason: `You do not have permission to change ${member.name} from ${member.role} to ${newRole}.`
    }
  }

  const adminCount = countAdmins(members)
  if (member.role === 'admin' && newRole !== 'admin' && adminCount <= 1) {
    return { allowed: false, reason: 'Cannot demote the only admin in the workspace.' }
  }

  if (member.id === currentUserId && member.role === 'admin' && newRole !== 'admin' && adminCount <= 1) {
    return { allowed: false, reason: 'You cannot demote yourself when you are the only admin.' }
  }

  return { allowed: true }
}

export const canRemoveMember = ({
  member,
  members,
  currentUserId,
  currentUserRole
}: {
  member: WorkspaceMemberIdentity
  members: WorkspaceMemberIdentity[]
  currentUserId: number | undefined
  currentUserRole: MemberRole | undefined
}): { allowed: true } | { allowed: false; reason: string } => {
  if (currentUserRole == null) {
    return { allowed: false, reason: 'Unable to determine your role in this workspace.' }
  }

  if (member.id === currentUserId) {
    return {
      allowed: false,
      reason: 'You cannot remove yourself with this tool. Use Leave workspace from the members menu instead.'
    }
  }

  const roleRemovePermissions: Record<MemberRole, readonly MemberRole[]> = {
    admin: PERMISSIONS.REMOVE_ADMIN_FROM_WORKSPACE as readonly MemberRole[],
    editor: PERMISSIONS.REMOVE_EDITOR_FROM_WORKSPACE as readonly MemberRole[],
    viewer: PERMISSIONS.REMOVE_VIEWER_FROM_WORKSPACE as readonly MemberRole[]
  }

  if (!roleRemovePermissions[member.role].includes(currentUserRole)) {
    return {
      allowed: false,
      reason: `You do not have permission to remove a ${member.role} from this workspace.`
    }
  }

  if (member.role === 'admin' && countAdmins(members) <= 1) {
    return { allowed: false, reason: 'Cannot remove the only admin in the workspace.' }
  }

  return { allowed: true }
}

export const findMemberByIdOrEmail = (
  members: WorkspaceMemberIdentity[],
  memberIdOrEmail: string
): WorkspaceMemberIdentity | undefined => {
  const needle = memberIdOrEmail.trim().toLowerCase()
  return members.find(
    (candidate) => String(candidate.id).toLowerCase() === needle || candidate.email.toLowerCase() === needle
  )
}

export const findPendingInvite = (
  messages: MessageResp[] | undefined,
  inviteIdOrEmail: string
): MessageResp | undefined => {
  const pending = getPendingInvites(messages)
  const needle = normalizeEmail(inviteIdOrEmail)
  return pending.find(
    (message) => String(message.id).toLowerCase() === needle || normalizeEmail(message.toEmail) === needle
  )
}

/** Coerce a form/API severity value to an array. Disabled RHF fields and partial API payloads can be undefined. */
export const toSeverityArray = (value: unknown): Severity[] => (Array.isArray(value) ? [...value] : [])

export const transformFormDataToApiPayload = (formData: NotificationsSettingsFormData): ApiNotificationSettings => {
  return {
    datasources: {
      on: formData.datasources.on,
      severity: formData.datasources.severity
    },
    job_statuses: {
      on: formData.jobStatuses.on,
      severity: formData.jobStatuses.severity,
      connectors: {
        on: formData.jobStatuses.connectors.on
      },
      reporting_solutions: {
        on: formData.jobStatuses.reportingSolutions.on
      },
      attribution_models: {
        on: formData.jobStatuses.attributionModels.on
      },
      budget_optimiser: {
        on: formData.jobStatuses.budgetOptimiser.on
      },
      enhanced_signals: {
        on: formData.jobStatuses.enhancedSignals.on
      }
    },
    recommendations: {
      on: formData.recommendations.on,
      severity: formData.recommendations.severity
    }
  }
}

export const apiNotificationSettingsToFormData = (
  settings: ApiNotificationSettings | NotificationsSettingsFormData | undefined | null
): NotificationsSettingsFormData | undefined => {
  if (settings == null) return undefined

  if ('jobStatuses' in settings && settings.jobStatuses != null) {
    return {
      ...settings,
      jobStatuses: {
        ...settings.jobStatuses,
        severity: toSeverityArray(settings.jobStatuses.severity)
      },
      recommendations: {
        ...settings.recommendations,
        severity: toSeverityArray(settings.recommendations?.severity)
      },
      datasources: {
        ...settings.datasources,
        severity: toSeverityArray(settings.datasources?.severity)
      }
    }
  }

  if (!('job_statuses' in settings) || settings.job_statuses == null) {
    return undefined
  }

  return {
    jobStatuses: {
      on: settings.job_statuses.on,
      severity: toSeverityArray(settings.job_statuses.severity),
      connectors: { on: settings.job_statuses.connectors.on },
      reportingSolutions: { on: settings.job_statuses.reporting_solutions.on },
      attributionModels: { on: settings.job_statuses.attribution_models.on },
      budgetOptimiser: { on: settings.job_statuses.budget_optimiser.on },
      enhancedSignals: { on: settings.job_statuses.enhanced_signals.on }
    },
    recommendations: {
      on: settings.recommendations.on,
      severity: toSeverityArray(settings.recommendations.severity)
    },
    datasources: {
      on: settings.datasources.on,
      severity: toSeverityArray(settings.datasources.severity)
    }
  }
}

export const workspaceRoleFilterToDropdownItems = (roles: MemberRole[]): Array<{ label: string; value: MemberRole }> =>
  roles.map((role) => ({
    label: role.charAt(0).toUpperCase() + role.slice(1),
    value: role
  }))

export const workspaceDropdownItemsToRoles = (items: Array<{ label: string; value: string }>): MemberRole[] =>
  items.map((item) => item.value as MemberRole)
